import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Tag,
  Row,
  Col,
  Input,
  Select,
  Button,
  DatePicker,
} from "antd";
import jsPDF from "jspdf";
import {
  CalendarOutlined,
  SearchOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import moment from "moment";

const MaturityScheduleReport = ({ investments }) => {
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState([]);

  // Process maturity dates
  const processInvestments = (data) => {
    return data.map((investment) => ({
      ...investment,
      maturity_date: moment(investment.investment_date)
        .add(investment.term_days, "days")
        .format("YYYY-MM-DD"),
      days_remaining: moment(investment.maturity_date).diff(moment(), "days"),
    }));
  };

  // Columns configuration
  const columns = [
    {
      title: "Investor",
      dataIndex: "investor",
      render: (_, record) => (
        <div>
          <div className="investor-name">{record.investor_id.first_name}</div>
          <div className="investor-company">{record.investor_id.last_name}</div>
        </div>
      ),
      sorter: (a, b) =>
        a.investor_id.first_name.localeCompare(b.investor_id.first_name),
    },
    {
      title: "Principal Amount",
      dataIndex: "principal_amount",
      render: (amount) => `ZMW ${amount.toLocaleString()}`,
      sorter: (a, b) => a.principal_amount - b.principal_amount,
    },
    {
      title: "Investment Date",
      dataIndex: "investment_date",
      render: (date) => moment(date).format("DD MMM YYYY"),
      sorter: (a, b) => moment(a.investment_date) - moment(b.investment_date),
    },
    {
      title: "Maturity Date",
      dataIndex: "maturity_date",
      render: (date) => moment(date).format("DD MMM YYYY"),
      sorter: (a, b) => moment(a.maturity_date) - moment(b.maturity_date),
    },
    {
      title: "Days Remaining",
      dataIndex: "days_remaining",
      render: (days) => (
        <Tag color={days > 30 ? "green" : days > 7 ? "orange" : "red"}>
          {days} days
        </Tag>
      ),
      sorter: (a, b) => a.days_remaining - b.days_remaining,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={status === "Active" ? "green" : "purple"}>
          {status.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: "Active", value: "active" },
        { text: "Matured", value: "matured" },
        { text: "Withdrawn", value: "withdrawn" },
      ],
      onFilter: (value, record) => record.status === value,
    },
  ];

  // Filter handlers
  useEffect(() => {
    let data = processInvestments(investments);

    // Apply text search
    if (searchText) {
      data = data.filter(
        (item) =>
          item.investor_id.first_name
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          item.investor_id.last_name
            .toLowerCase()
            .includes(searchText.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      data = data.filter((item) => item.status === statusFilter);
    }

    // Apply date range filter
    if (dateRange.length === 2) {
      data = data.filter((item) =>
        moment(item.maturity_date).isBetween(
          dateRange[0],
          dateRange[1],
          null,
          "[]"
        )
      );
    }

    setFilteredData(data);
  }, [investments, searchText, statusFilter, dateRange]);

  // Export handlers
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [columns.map((col) => col.title)],
      body: filteredData.map((item) => [
        item.investor_id.first_name,
        `ZMW ${item.principal_amount.toLocaleString()}`,
        moment(item.investment_date).format("DD MMM YYYY"),
        moment(item.maturity_date).format("DD MMM YYYY"),
        `${item.days_remaining} days`,
        item.status.toUpperCase(),
      ]),
      theme: "grid",
      headStyles: { fillColor: [41, 128, 185] },
    });
    doc.save("maturity-schedule.pdf");
  };

  const exportCSV = () => {
    // const csvExporter = new ExportToCsv({
    //   filename: "maturity-schedule",
    //   showLabels: true,
    //   headers: columns.map((col) => col.title),
    // });
    // const csvData = filteredData.map((item) => ({
    //   Investor: item.investor.name,
    //   Company: item.investor.company,
    //   "Principal Amount": `ZMW ${item.principal_amount.toLocaleString()}`,
    //   "Investment Date": moment(item.investment_date).format("YYYY-MM-DD"),
    //   "Maturity Date": moment(item.maturity_date).format("YYYY-MM-DD"),
    //   "Days Remaining": item.days_remaining,
    //   Status: item.status.toUpperCase(),
    // }));
    // csvExporter.generateCsv(csvData);
  };

  return (
    <Card
      title={
        <div className="report-header">
          <CalendarOutlined />
          <span style={{ marginLeft: 8 }}>Maturity Schedule Report</span>
        </div>
      }
      extra={
        <div className="export-buttons">
          <Button icon={<FilePdfOutlined />} onClick={exportPDF}>
            PDF
          </Button>
          <Button
            icon={<FileExcelOutlined />}
            onClick={exportCSV}
            style={{ marginLeft: 8 }}
          >
            Excel
          </Button>
        </div>
      }
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Input
            placeholder="Search investors..."
            prefix={<SearchOutlined />}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>
        <Col span={8}>
          <Select
            style={{ width: "100%" }}
            defaultValue="all"
            onChange={setStatusFilter}
            options={[
              { value: "all", label: "All Statuses" },
              { value: "active", label: "Active" },
              { value: "matured", label: "Matured" },
            ]}
          />
        </Col>
        <Col span={8}>
          <DatePicker.RangePicker
            style={{ width: "100%" }}
            onChange={(dates) => setDateRange(dates)}
            disabledDate={(current) =>
              current && current < moment().startOf("day")
            }
          />
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="id"
        pagination={{
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
          showTotal: (total) => `Total ${total} investments`,
        }}
        scroll={{ x: true }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={3}>
                <strong>Total Principal:</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <strong>
                  ZMW{" "}
                  {filteredData
                    .reduce((sum, item) => sum + item.principal_amount, 0)
                    .toLocaleString()}
                </strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </Card>
  );
};

// PropTypes would be recommended here
export default MaturityScheduleReport;
